import { Routes, Route, Navigate, Outlet, RoutesProps } from "react-router-dom";

import React, { Suspense } from "react";
import ProtectedRoute from "./ProtectedRoute";
import Main from "../components/Main";
import { RootState } from "../config/Store";
import { UserState } from "../reducer/AuthReducer";
import { useSelector } from "react-redux";
import CustomerWallet from "../pages/CustomerWallet/CustomerWallet";
const Navigation = () => {
  interface ProtectedRouteProps extends RoutesProps {
    isAuthenticated: boolean;
    authenticationPath: string;
  }

  const login: any = useSelector<RootState, UserState>((state: any) => state.userLogin);
  const userInfoData: any = useSelector<RootState, any>(
    (state: any) => state.userInfoData,
  );

  // Vendors get their own dashboard on the same route: the menu, guards and
  // redirects all point at /dashboard already.
  const isVendorUser = Boolean(userInfoData?.user_info?.vendorId);
  // /me is fetched after mount, so user_info is empty on the first render.
  // Deciding before it arrives would flash an admin screen at a vendor.
  const isUserLoaded = Boolean(
    userInfoData?.user_info?.email || userInfoData?.user_info?.id,
  );

  // Guard element for the admin-only layout route. Written as an element rather
  // than a component declared here: a component defined inside Navigation gets a
  // new identity on every render, which would unmount and remount the whole
  // admin subtree (losing page state) each time redux user_info changes.
  const adminOutlet = !isUserLoaded ? (
    <></>
  ) : isVendorUser ? (
    <Navigate replace to="/dashboard" />
  ) : (
    <Outlet />
  );

  // element = { defaultProtectedRouteProps.isAuthenticated || login.loginSuccess ? (<Navigate replace to="/dashboard" />) : item.component }

  const defaultProtectedRouteProps: Omit<ProtectedRouteProps, "outlet"> = {
    isAuthenticated: localStorage.getItem("token") != null,
    authenticationPath: "/login",
  };

  const Login = React.lazy(() => import("../components/Login/Login"));
  const ForgotPassword = React.lazy(
    () => import("../components/Login/ForgotPassword"),
  );
  const RevenueShare = React.lazy(
    () => import("../pages/RevenueShare/RevenueShare"),
  );
  const Dashboard = React.lazy(() => import("../pages/Dashboard/Dashboard"));
  const VendorDashboard = React.lazy(
    () => import("../pages/Dashboard/VendorDashboard/VendorDashboard"),
  );
  const Charging = React.lazy(() => import("../pages/Charging/Charging"));
  const ChargingPointDetail = React.lazy(() => import("../pages/Charging/ChargingPointDetail"));
  const Station = React.lazy(() => import("../pages/Station/Station"));
  const Users = React.lazy(() => import("../pages/Users/Users"));
  const Bookings = React.lazy(() => import("../pages/Bookings/Bookings"));
  const VendorManagement = React.lazy(() => import("../pages/VendorManagement/VendorManagement"));
  const ExpenseManagement = React.lazy(
    () => import("../pages/ExpenseManagement/ExpenseManagement"),
  );
  const Feedback = React.lazy(() => import("../pages/Feedback/Feedback"));
  const Feedbackdetails = React.lazy(() => import("../pages/Feedbackdetails/Feedbackdetails"));
  const ChargingdetailsTab = React.lazy(() => import("../pages/Charging/Chargin.details.tab"));
  const ServiceMonitoring = React.lazy(() => import("../pages/Service-Monitoring/Service.Monitoring"));
  const CustomerDatabase = React.lazy(() => import("../pages/Customer-Database/Customer.Database"));
  const ServiceLogs = React.lazy(() => import("../pages/Service-Monitoring/ServiceLogs"));
  const DowntimeLogs = React.lazy(() => import("../pages/Service-Monitoring/DowntimeLogs"));
  const CustomerBookingDetail = React.lazy(() => import("../pages/Customer-Database/CustomerBookingDetail"));
  const StateWiseRevenue = React.lazy(() => import("../pages/Dashboard/StateWiseRevenue"));
  const VehicleModal = React.lazy(() => import("../pages/Vehicle-modal/VehicleModal"));
  const StationMeterDetail = React.lazy(() => import("../pages/StationMeterDetail/StationMeterDetail"));
  const CMS = React.lazy(() => import("../pages/CMS/CMS"));
  const PrivacyPolicy = React.lazy(() => import("../pages/CMS/PrivacyPoicy"));
  const TermCondition = React.lazy(() => import("../pages/CMS/TermCondition"));
  const HelpSupport = React.lazy(() => import("../pages/HelpSupport/HelpSupport"));
  const ForceUpdate = React.lazy(() => import("../pages/ForceUpdate/ForceUpdate"));
  const BookingLogs = React.lazy(() => import("../pages/BookingLogs/BookingLogs"));
  const CustomerWallet = React.lazy(() => import("../pages/CustomerWallet/CustomerWallet"));
  const MachineManagement = React.lazy(() => import("../pages/MachineManagement/MachineManagement"));

  return (
    <>
      <div id="main-wraper">
        <Routes>
          <Route
            path="/PrivacyPolicy"
            element={
              <Suspense fallback={<></>}>
                {" "}
                <PrivacyPolicy />{" "}
              </Suspense>
            }
          />
          <Route
            path="/TermCondition"
            element={
              <Suspense fallback={<></>}>
                {" "}
                <TermCondition />{" "}
              </Suspense>
            }
          />

          <Route
            path="/login"
            element={
              <Suspense fallback={<></>}>
                {defaultProtectedRouteProps.isAuthenticated || login.loginSuccess ? (
                  <Navigate replace to="/dashboard" />
                ) : (
                  <Login />
                )}
              </Suspense>
            }
          />-
          <Route
            path="/forgot-password"
            element={
              <Suspense fallback={<></>}>
                {defaultProtectedRouteProps.isAuthenticated || login.loginSuccess ? (
                  <Navigate replace to="/dashboard" />
                ) : (
                  <ForgotPassword />
                )}
              </Suspense>
            }
          />
          <Route
            path="/"
            element={
              defaultProtectedRouteProps.isAuthenticated || login.loginSuccess ? (
                <Navigate replace to="/dashboard" />
              ) : (
                <Navigate replace to="/login" />
              )
            }
          />
          <Route path="/" element={<ProtectedRoute {...defaultProtectedRouteProps} outlet={<Main />} />}>
            <Route
              path="/dashboard"
              element={
                <Suspense fallback={<></>}>
                  {isVendorUser ? <VendorDashboard /> : <Dashboard />}
                </Suspense>
              }
            />
            <Route
              path="/revenue-share"
              element={
                <Suspense fallback={<></>}>
                  <RevenueShare />
                </Suspense>
              }
            />
            {/* Everything below is admin-only. A vendor login has exactly two
                screens - their Dashboard and their Revenue Share Calculation -
                so typing any other URL sends them back to the dashboard. */}
            <Route element={adminOutlet}>
            <Route
              path="/expense-management"
              element={
                <Suspense fallback={<></>}>
                  <ExpenseManagement />
                </Suspense>
              }
            />
            <Route
              path="/state-wise-revenue"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <StateWiseRevenue />{" "}
                </Suspense>
              }
            />
            <Route
              path="/charging"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <Charging />{" "}
                </Suspense>
              }
            />
            <Route
              path="/charging-details"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <ChargingdetailsTab />{" "}
                </Suspense>
              }
            />
            <Route
              path="/station"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <Station />{" "}
                </Suspense>
              }
            />
            <Route
              path="/users"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <Users />{" "}
                </Suspense>
              }
            />
            <Route
              path="/bookings"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <Bookings />{" "}
                </Suspense>
              }
            />
            <Route
              path="/vendor-management"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <VendorManagement />{" "}
                </Suspense>
              }
            />
            <Route
              path="/customer-database"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <CustomerDatabase />{" "}
                </Suspense>
              }
            />
            <Route
              path="/feedback"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <Feedback />{" "}
                </Suspense>
              }
            />
            <Route
              path="/feedbackdetails"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <Feedbackdetails />{" "}
                </Suspense>
              }
            />
            <Route
              path="/service-monitoring"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <ServiceMonitoring />{" "}
                </Suspense>
              }
            />
            <Route
              path="/ServiceLogs/:id"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <ServiceLogs />{" "}
                </Suspense>
              }
            />
            <Route
              path="/DowntimeLogs/:id"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <DowntimeLogs />{" "}
                </Suspense>
              }
            />
            <Route
              path="/CustomerBookingDetail"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <CustomerBookingDetail />{" "}
                </Suspense>
              }
            />
            <Route
              path="/vehicle-modal"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <VehicleModal />{" "}
                </Suspense>
              }
            />
            <Route
              path="/stationMeterDetail"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <StationMeterDetail />{" "}
                </Suspense>
              }
            />
            <Route
              path="/ChargingPointDetail"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <ChargingPointDetail />{" "}
                </Suspense>
              }
            />
            <Route
              path="/cms"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <CMS />{" "}
                </Suspense>
              }
            />
            
            <Route
              path="/HelpSupport"
              element={
                <Suspense fallback={<></>}>
                  {" "}
                  <HelpSupport />{" "}
                </Suspense>
              }
            />
            {/* Force Update Settings - 25/09/2026. Inside the same protected
                block as every other admin route, so it inherits the auth gate
                rather than inventing one. */}
            <Route
              path="/force-update"
              element={
                <Suspense fallback={<></>}>
                  <ForceUpdate />
                </Suspense>
              }
            />
            {/* Booking Logs - 25/09/2026. Same protected block, so it inherits
                the auth gate. The API behind it is admin-guarded as well, which
                matters because these rows carry customer IPs. */}
            <Route
              path="/booking-logs"
              element={
                <Suspense fallback={<></>}>
                  <BookingLogs />
                </Suspense>
              }
            />
            <Route
              path="/customer-wallets"
              element={
                <Suspense fallback={<></>}>
                  <CustomerWallet />
                </Suspense>
              }
            />
            <Route
              path="/machine-management"
              element={
                <Suspense fallback={<></>}>
                  <MachineManagement />
                </Suspense>
              }
            />
            </Route>
          </Route>
        </Routes>
      </div>
    </>
  );
};

export default Navigation;
